import "./SectionSidebarOptionsMenu.scss";

import { FC, Ref, useRef, useState } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { CancelSaveWidget } from "mds/components/CancelSaveWidget";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import {
  ArrowDownIcon,
  CopySendArrowIcon,
  EyeNotVisibleIcon,
  EyeVisibleIcon,
  GearSettingsIcon,
} from "mds/icons";
import { useAppSelector } from "store/index";
import { termSelectors } from "store/selectors";

interface SectionSidebarOptionsMenuProps extends SidebarOptionsMenuProps {
  section: SectionType;
  optionsMenuActions: SidebarOptionsMenuProps["optionsMenuActions"] & {
    onSettings?: () => void;
    onCopyTo?: (sectionId: string, courseId?: string, termId?: string) => void;
    onMoveTo?: (courseId: string, termId: string) => void;
    onExport?: () => void;
    onArchive?: () => void;
    onUnarchive?: () => void;
  };
}

export const SectionSidebarOptionsMenu: FC<SectionSidebarOptionsMenuProps> = ({
  styles,
  size = "s",
  className,
  direction = "right",
  section,
  optionsMenuActions,
}) => {
  const sectionSidebarMenuRef: Ref<MenuInstance> = useRef(null);
  const [selectedCourseId, setSelectedCourseId] = useState(section.course_id);
  const [selectedTermId, setSelectedTermId] = useState(section.term_id);
  const term = useAppSelector((s) => termSelectors.selectById(s, section.term_id));
  const termDisabled = term?.archived_at !== null;

  // NOTE: We use the same state for both copy and move to, this should be fine as these menus will never
  // render at the same time
  const handleCourseChange = (newCourseId: string) => {
    setSelectedCourseId(newCourseId);
  };

  const handleTermChange = (newTermId: string) => {
    setSelectedTermId(newTermId);
  };

  const copyToMenuItem = optionsMenuActions.onCopyTo && (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.copy_to")} />
      }
      withArrow
    >
      <div className="h4 m-2 min-w-[280px]">
        <div className="pb-2 text-black-tint-40">{t("common.copy_to")}</div>

        <div className="flex">
          <CourseMenu
            defaultToAll={false}
            selectedCourseId={selectedCourseId}
            onCourseChange={handleCourseChange}
          />

          <TermMenu
            defaultToAll={false}
            selectedTermId={selectedTermId}
            onTermChange={handleTermChange}
          />
        </div>

        <CancelSaveWidget
          className="right pt-2"
          saveTextKey="copy"
          onClickCancel={() => {
            sectionSidebarMenuRef.current.closeMenu();
          }}
          onClickSave={() =>
            optionsMenuActions.onCopyTo(section.id, selectedCourseId, selectedTermId)
          }
        />
      </div>
    </SubMenu>
  );

  const moveToMenuItem = optionsMenuActions.onMoveTo && (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_section")} />
      }
      withArrow
    >
      <div className="h4 m-2 min-w-[280px]">
        <div className="pb-2 text-black-tint-40">{t("sidebar_options_menu.move_section_to")}</div>

        <div className="flex">
          <CourseMenu
            defaultToAll={false}
            selectedCourseId={selectedCourseId}
            onCourseChange={handleCourseChange}
          />

          <TermMenu
            defaultToAll={false}
            selectedTermId={selectedTermId}
            onTermChange={handleTermChange}
          />
        </div>

        <CancelSaveWidget
          className="right pt-2"
          saveTextKey="move"
          onClickCancel={() => {
            sectionSidebarMenuRef.current.closeMenu();
          }}
          onClickSave={() => {
            optionsMenuActions.onMoveTo(selectedCourseId, selectedTermId);
            sectionSidebarMenuRef.current.closeMenu();
          }}
        />
      </div>
    </SubMenu>
  );

  const sectionSettingItem = optionsMenuActions.onSettings && (
    <MenuItem
      eventName="Admin Courses - Section Settings - Open"
      title={t("settings.section_settings")}
      onClick={optionsMenuActions.onSettings}
    >
      <IconText iconStart={<GearSettingsIcon />} text={t("settings.section_settings")} />
    </MenuItem>
  );

  const exportMenuItem = optionsMenuActions.onExport && (
    <MenuItem onClick={optionsMenuActions.onExport}>
      <IconText iconStart={<ArrowDownIcon />} text={t("common.export")} />
    </MenuItem>
  );

  const archiveMenuItem = !termDisabled && !section.archived_at && optionsMenuActions.onArchive && (
    <MenuItem onClick={optionsMenuActions.onArchive}>
      <IconText iconStart={<EyeNotVisibleIcon />} text={t("sidebar_options_menu.archive")} />
    </MenuItem>
  );

  const unarchiveMenuItem = (termDisabled || section.archived_at) &&
    optionsMenuActions.onUnarchive && (
      <MenuItem disabled={termDisabled} onClick={optionsMenuActions.onUnarchive}>
        <IconText iconStart={<EyeVisibleIcon />} text={t("sidebar_options_menu.unarchive")} />
      </MenuItem>
    );

  return (
    <SidebarOptionsMenu
      className={className}
      direction={direction}
      instanceRef={sectionSidebarMenuRef}
      optionsMenuActions={optionsMenuActions}
      size={size}
      styles={styles}
    >
      {sectionSettingItem}
      {copyToMenuItem}
      {moveToMenuItem}
      {exportMenuItem}
      {archiveMenuItem}
      {unarchiveMenuItem}
    </SidebarOptionsMenu>
  );
};
